import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { finesAPI, vehiclesAPI, driversAPI } from '../services/api';
import { toast } from 'sonner';
import { 
  Receipt, 
  Plus, 
  Loader2, 
  Calendar,
  CheckCircle2,
  Clock,
  ChevronRight,
  Edit,
  Trash2,
  Eye,
  Send,
  Check,
  X,
  Upload,
  Download,
  FileText,
  User,
  DollarSign,
  AlertTriangle,
  XCircle,
  File,
  MapPin,
  CreditCard
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUPPORTING_DOC_TYPES = {
  fine_notice: 'Fine Notice',
  payment_receipt: 'Payment Receipt',
  court_summons: 'Court Summons',
  representation_letter: 'Representation Letter',
  appeal_documents: 'Appeal Documents',
  other: 'Other Documents',
};

const getStatusBadge = (status) => {
  const styles = {
    draft: 'bg-slate-100 text-slate-700',
    submitted: 'bg-blue-100 text-blue-700',
    pending_approval: 'bg-amber-100 text-amber-700',
    approved: 'bg-emerald-100 text-emerald-700',
    returned: 'bg-red-100 text-red-700',
  };
  return styles[status] || 'bg-slate-100 text-slate-700';
};

const getStatusLabel = (status) => {
  const labels = {
    draft: 'Draft',
    submitted: 'Submitted',
    pending_approval: 'Pending Approval',
    approved: 'Approved',
    returned: 'Returned',
  };
  return labels[status] || status;
};

// Supporting Document Upload Component
const SupportingDocUpload = ({ docKey, label, docData, onUpload, onReasonChange, fineId, disabled }) => {
  const [uploading, setUploading] = useState(false);
  const hasFile = docData?.uploaded;
  const filename = docData?.filename;
  const reason = docData?.reason || '';

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!fineId) {
      toast.error('Please save the fine first before uploading documents');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('doc_type', docKey);
      
      const uploadResp = await axios.post(
        `${API_URL}/api/fines/${fineId}/upload-document`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const result = uploadResp.data;
      onUpload(docKey, result.filename);
      toast.success(`${label} uploaded successfully`);
    } catch (error) {
      toast.error(`Failed to upload ${label}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="border rounded-lg p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {hasFile ? (
            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
          ) : (
            <XCircle className="w-5 h-5 text-slate-300" />
          )}
          <span className={`text-sm font-medium ${hasFile ? 'text-emerald-700' : 'text-slate-600'}`}>
            {label}
          </span>
        </div>
        <label>
          <input
            type="file"
            className="hidden"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            onChange={handleFileUpload}
            disabled={disabled || uploading}
          />
          <Button
            variant={hasFile ? "outline" : "default"}
            size="sm"
            className="gap-1"
            disabled={disabled || uploading}
            asChild
          >
            <span>
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {hasFile ? 'Replace' : 'Upload'}
            </span>
          </Button>
        </label>
      </div>
      
      {hasFile && filename && (
        <div className="flex items-center gap-2 text-sm text-emerald-600 bg-emerald-50 rounded px-2 py-1">
          <File className="w-4 h-4" />
          <span className="truncate flex-1">{filename}</span>
          <a 
            href={`${API_URL}/api/uploads/fines/${filename}`}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline flex items-center gap-1 text-blue-600"
          >
            <Download className="w-4 h-4" />
          </a>
        </div>
      )}
      
      {!hasFile && (
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Reason if not uploaded</Label>
          <Input
            value={reason}
            onChange={(e) => onReasonChange(docKey, e.target.value)}
            placeholder="Optional - reason for not uploading"
            className="text-sm"
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
};

// Fine Detail Dialog
const FineDetailDialog = ({ open, onClose, fine, onApprove, onReturn, onMarkPaid, canApprove }) => {
  if (!fine) return null;

  const supportingDocs = fine.supporting_docs || {};

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-amber-500" />
            Fine Details - {fine.fine_number || 'No Reference'}
          </DialogTitle>
          <div className="flex items-center gap-2 mt-2">
            <Badge className={getStatusBadge(fine.status)}>
              {getStatusLabel(fine.status)}
            </Badge>
            <Badge className={fine.paid ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
              {fine.paid ? 'PAID' : 'UNPAID'}
            </Badge>
          </div>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Fine Details */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg">
            <div>
              <Label className="text-xs text-slate-500">Registration No</Label>
              <p className="font-mono font-bold">{fine.registration_no || fine.truck_registration}</p>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Fine Number</Label>
              <p className="font-mono">{fine.fine_number || 'N/A'}</p>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Offence Date</Label>
              <p>{fine.offence_date?.slice(0, 10) || 'N/A'}</p>
            </div>
            <div>
              <Label className="text-xs text-slate-500">District</Label>
              <p>{fine.district || 'N/A'}</p>
            </div>
          </div>

          {/* Amount & Payment */}
          <div className={`p-4 rounded-lg ${fine.paid ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Amount</Label>
                <p className="text-2xl font-bold">R {fine.amount || 0}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Paid Status</Label>
                <p className={`font-semibold ${fine.paid ? 'text-emerald-600' : 'text-red-600'}`}>
                  {fine.paid ? 'PAID' : 'UNPAID'}
                </p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">AARTO Points</Label>
                <p className="font-semibold">{fine.aarto_points || 0}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Total Offences</Label>
                <p className="font-semibold">{fine.total_offences || 1}</p>
              </div>
            </div>
          </div>

          {/* Offence Details */}
          <div className="space-y-3">
            <h3 className="font-semibold text-slate-900 border-b pb-2">Offence Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Offence</Label>
                <p className="text-sm bg-slate-50 p-2 rounded">{fine.offence || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Charge</Label>
                <p className="text-sm bg-slate-50 p-2 rounded">{fine.charge || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Responsible</Label>
                <p className="text-sm bg-slate-50 p-2 rounded">{fine.responsible || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">AOD</Label>
                <p className="text-sm bg-slate-50 p-2 rounded">{fine.aod || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Driver Details */}
          <div className="space-y-3">
            <h3 className="font-semibold text-slate-900 border-b pb-2">Driver Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Driver Name</Label>
                <p>{fine.driver_name || 'Unknown'}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Driver No</Label>
                <p>{fine.driver_no || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Action Taken */}
          {fine.action_taken && (
            <div className="space-y-2">
              <h3 className="font-semibold text-slate-900 border-b pb-2">Action Taken</h3>
              <p className="text-sm bg-slate-50 p-3 rounded">{fine.action_taken}</p>
            </div>
          )}

          {/* Supporting Documents */}
          <div className="space-y-3">
            <h3 className="font-semibold text-slate-900 border-b pb-2 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Supporting Documents
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.entries(SUPPORTING_DOC_TYPES).map(([key, label]) => {
                const doc = supportingDocs[key] || {};
                const isUploaded = typeof doc === 'boolean' ? doc : doc.uploaded;
                const filename = typeof doc === 'object' ? doc.filename : null;
                
                return (
                  <div key={key} className="flex items-center justify-between p-3 border rounded">
                    <div className="flex items-center gap-2">
                      {isUploaded ? (
                        <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                      ) : (
                        <XCircle className="w-5 h-5 text-slate-300" />
                      )}
                      <span className="text-sm">{label}</span>
                    </div>
                    {isUploaded && filename ? (
                      <a 
                        href={`${API_URL}/api/uploads/fines/${filename}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                      >
                        <Download className="w-3 h-3" />
                        Download
                      </a>
                    ) : (
                      <span className="text-xs text-slate-400">Not uploaded</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Uploaded Documents List */}
          {fine.uploaded_documents && fine.uploaded_documents.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-semibold text-slate-900 border-b pb-2 flex items-center gap-2">
                <File className="w-4 h-4" />
                All Uploaded Files ({fine.uploaded_documents.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {fine.uploaded_documents.map((filename, idx) => (
                  <a
                    key={idx}
                    href={`${API_URL}/api/uploads/fines/${filename}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-2 border rounded hover:bg-slate-50 transition-colors"
                  >
                    <File className="w-4 h-4 text-blue-500" />
                    <span className="text-sm text-blue-600 truncate flex-1">{filename}</span>
                    <Download className="w-4 h-4 text-slate-400" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {fine.notes && (
            <div className="space-y-2">
              <h3 className="font-semibold text-slate-900 border-b pb-2">Notes</h3>
              <p className="text-sm bg-slate-50 p-3 rounded">{fine.notes}</p>
            </div>
          )}

          {/* Submission Info */}
          <div className="grid grid-cols-2 gap-4 p-3 bg-slate-100 rounded text-sm">
            <div>
              <span className="text-slate-500">Created by:</span>
              <span className="ml-2">{fine.created_by_name}</span>
            </div>
            <div>
              <span className="text-slate-500">Submitted by:</span>
              <span className="ml-2">{fine.submitted_by_name || 'Not submitted'}</span>
            </div>
            {fine.approved_by_name && (
              <div>
                <span className="text-slate-500">Approved by:</span>
                <span className="ml-2">{fine.approved_by_name}</span>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          {!fine.paid && fine.status === 'approved' && (
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => {
                onMarkPaid(fine);
                onClose();
              }}
            >
              <CreditCard className="w-4 h-4 mr-1" />
              Mark as Paid
            </Button>
          )}
          {canApprove && fine.status === 'pending_approval' && (
            <>
              <Button
                variant="destructive"
                onClick={() => {
                  const reason = prompt('Reason for returning:');
                  if (reason) {
                    onReturn(fine, reason);
                    onClose();
                  }
                }}
              >
                <X className="w-4 h-4 mr-1" />
                Return
              </Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => {
                  onApprove(fine);
                  onClose();
                }}
              >
                <Check className="w-4 h-4 mr-1" />
                Approve
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Fine Form Dialog
const FineFormDialog = ({ open, onClose, fine, onSave, vehicles, drivers, onRefresh }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [savedFineId, setSavedFineId] = useState(fine?.id || null);
  
  const initSupportingDocs = () => {
    const docs = {};
    Object.keys(SUPPORTING_DOC_TYPES).forEach(key => {
      docs[key] = { uploaded: false, filename: '', reason: '' };
    });
    return docs;
  };

  const [formData, setFormData] = useState({
    registration_no: '',
    fine_number: '',
    offence: '',
    offence_date: new Date().toISOString().slice(0, 10),
    district: '',
    amount: '',
    paid: false,
    responsible: '',
    driver_no: '',
    driver_name: '',
    action_taken: '',
    charge: '',
    aod: '',
    total_offences: 1,
    aarto_points: 0,
    truck_id: '',
    driver_id: '',
    supporting_docs: initSupportingDocs(),
    notes: '',
  });

  useEffect(() => {
    if (fine) {
      setSavedFineId(fine.id);
      const existingDocs = fine.supporting_docs || {};
      const convertedDocs = {};
      Object.keys(SUPPORTING_DOC_TYPES).forEach(key => {
        if (typeof existingDocs[key] === 'boolean') {
          convertedDocs[key] = { uploaded: existingDocs[key], filename: '', reason: '' };
        } else {
          convertedDocs[key] = existingDocs[key] || { uploaded: false, filename: '', reason: '' };
        }
      });

      setFormData({
        registration_no: fine.registration_no || fine.truck_registration || '',
        fine_number: fine.fine_number || '',
        offence: fine.offence || '',
        offence_date: fine.offence_date?.slice(0, 10) || new Date().toISOString().slice(0, 10),
        district: fine.district || '',
        amount: fine.amount || '',
        paid: fine.paid || false,
        responsible: fine.responsible || '',
        driver_no: fine.driver_no || '',
        driver_name: fine.driver_name || '',
        action_taken: fine.action_taken || '',
        charge: fine.charge || '',
        aod: fine.aod || '',
        total_offences: fine.total_offences || 1,
        aarto_points: fine.aarto_points || 0,
        truck_id: fine.truck_id || '',
        driver_id: fine.driver_id || '',
        supporting_docs: convertedDocs,
        notes: fine.notes || '',
      });
    } else {
      setSavedFineId(null);
      setFormData({
        registration_no: '',
        fine_number: '',
        offence: '',
        offence_date: new Date().toISOString().slice(0, 10),
        district: '',
        amount: '',
        paid: false,
        responsible: '',
        driver_no: '',
        driver_name: '',
        action_taken: '',
        charge: '',
        aod: '',
        total_offences: 1,
        aarto_points: 0,
        truck_id: '',
        driver_id: '',
        supporting_docs: initSupportingDocs(),
        notes: '',
      });
    }
  }, [fine, open]);

  const updateField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleDocUpload = useCallback((docKey, filename) => {
    setFormData(prev => ({
      ...prev,
      supporting_docs: {
        ...prev.supporting_docs,
        [docKey]: { uploaded: true, filename, reason: '' }
      }
    }));
  }, []);

  const handleDocReasonChange = useCallback((docKey, reason) => {
    setFormData(prev => ({
      ...prev,
      supporting_docs: {
        ...prev.supporting_docs,
        [docKey]: { ...prev.supporting_docs[docKey], reason }
      }
    }));
  }, []);

  const handleSave = async () => {
    if (!formData.fine_number) {
      toast.error('Please enter a fine number');
      return;
    }

    setLoading(true);
    try {
      const result = await onSave(formData);
      if (result?.id) {
        setSavedFineId(result.id);
      }
      onClose();
    } catch (error) {
      console.error('Error saving fine:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!formData.fine_number) {
      toast.error('Please enter a fine number');
      return;
    }

    setLoading(true);
    try {
      const result = await onSave(formData);
      if (result?.id) {
        setSavedFineId(result.id);
        toast.success('Draft saved. You can now upload documents.');
      }
    } catch (error) {
      console.error('Error saving draft:', error);
    } finally {
      setLoading(false);
    }
  };

  // Auto-populate registration when truck is selected
  const handleTruckChange = (truckId) => {
    updateField('truck_id', truckId);
    const truck = vehicles.find(v => v.id === truckId);
    if (truck) {
      updateField('registration_no', truck.registration_number);
    }
  };

  // Auto-populate driver name when driver is selected
  const handleDriverChange = (driverId) => {
    updateField('driver_id', driverId);
    const driver = drivers.find(d => d.id === driverId);
    if (driver) {
      updateField('driver_name', `${driver.first_name || driver.user?.first_name || ''} ${driver.last_name || driver.user?.last_name || ''}`.trim() || driver.employee_number);
    }
  };

  const trucks = vehicles.filter(v => v.vehicle_type === 'Truck' || v.vehicle_type === 'truck');

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-amber-500" />
            {fine ? 'Edit Fine' : 'New Fine'}
          </DialogTitle>
          <DialogDescription>
            Capture traffic fine details. A corrective action will be automatically created.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-900 border-b pb-2">Fine Information</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Fine Number *</Label>
                <Input
                  value={formData.fine_number}
                  onChange={(e) => updateField('fine_number', e.target.value)}
                  placeholder="e.g., FN12345"
                />
              </div>
              <div className="space-y-2">
                <Label>Offence Date *</Label>
                <Input
                  type="date"
                  value={formData.offence_date}
                  onChange={(e) => updateField('offence_date', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>District</Label>
                <Input
                  value={formData.district}
                  onChange={(e) => updateField('district', e.target.value)}
                  placeholder="e.g., Johannesburg"
                />
              </div>
              <div className="space-y-2">
                <Label>Vehicle</Label>
                <Select value={formData.truck_id || 'none'} onValueChange={(v) => handleTruckChange(v === 'none' ? '' : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select vehicle" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select vehicle</SelectItem>
                    {trucks.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.registration_number} - {t.make} {t.model}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Registration No</Label>
                <Input
                  value={formData.registration_no}
                  onChange={(e) => updateField('registration_no', e.target.value)}
                  placeholder="Vehicle registration"
                />
              </div>
              <div className="space-y-2">
                <Label>Amount (R)</Label>
                <Input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => updateField('amount', parseFloat(e.target.value) || 0)}
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          {/* Offence Details */}
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-900 border-b pb-2">Offence Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Offence</Label>
                <Textarea
                  value={formData.offence}
                  onChange={(e) => updateField('offence', e.target.value)}
                  placeholder="Describe the offence"
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label>Charge</Label>
                <Textarea
                  value={formData.charge}
                  onChange={(e) => updateField('charge', e.target.value)}
                  placeholder="Charge details"
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label>AOD</Label>
                <Input
                  value={formData.aod}
                  onChange={(e) => updateField('aod', e.target.value)}
                  placeholder="AOD"
                />
              </div>
              <div className="space-y-2">
                <Label>AARTO Points</Label>
                <Input
                  type="number"
                  value={formData.aarto_points}
                  onChange={(e) => updateField('aarto_points', parseInt(e.target.value) || 0)}
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label>Total Offences</Label>
                <Input
                  type="number"
                  value={formData.total_offences}
                  onChange={(e) => updateField('total_offences', parseInt(e.target.value) || 1)}
                  placeholder="1"
                />
              </div>
            </div>
          </div>

          {/* Driver & Responsibility */}
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-900 border-b pb-2">Driver & Responsibility</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Driver</Label>
                <Select value={formData.driver_id || 'none'} onValueChange={(v) => handleDriverChange(v === 'none' ? '' : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select driver" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select driver</SelectItem>
                    {drivers.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.first_name || d.user?.first_name || ''} {d.last_name || d.user?.last_name || ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Driver Name</Label>
                <Input
                  value={formData.driver_name}
                  onChange={(e) => updateField('driver_name', e.target.value)}
                  placeholder="Driver name"
                />
              </div>
              <div className="space-y-2">
                <Label>Driver No</Label>
                <Input
                  value={formData.driver_no}
                  onChange={(e) => updateField('driver_no', e.target.value)}
                  placeholder="Driver number"
                />
              </div>
              <div className="space-y-2">
                <Label>Responsible</Label>
                <Input
                  value={formData.responsible}
                  onChange={(e) => updateField('responsible', e.target.value)}
                  placeholder="Who is responsible"
                />
              </div>
              <div className="space-y-2 flex items-end">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="paid"
                    checked={formData.paid}
                    onCheckedChange={(v) => updateField('paid', v)}
                  />
                  <Label htmlFor="paid" className="font-semibold text-emerald-600">
                    Fine Paid
                  </Label>
                </div>
              </div>
            </div>
          </div>

          {/* Action Taken */}
          <div className="space-y-2">
            <Label>Action Taken</Label>
            <Textarea
              value={formData.action_taken}
              onChange={(e) => updateField('action_taken', e.target.value)}
              placeholder="Describe the action taken..."
              rows={3}
            />
          </div>

          {/* Supporting Documents */}
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b pb-2">
              <h3 className="font-semibold text-slate-900">Supporting Documents</h3>
              {!savedFineId && (
                <Button variant="outline" size="sm" onClick={handleSaveDraft} disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  Save Draft to Enable Uploads
                </Button>
              )}
            </div>
            {!savedFineId && (
              <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded">
                Save the fine as a draft first to enable document uploads.
              </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.entries(SUPPORTING_DOC_TYPES).map(([key, label]) => (
                <SupportingDocUpload
                  key={key}
                  docKey={key}
                  label={label}
                  docData={formData.supporting_docs[key]}
                  onUpload={handleDocUpload}
                  onReasonChange={handleDocReasonChange}
                  fineId={savedFineId}
                  disabled={!savedFineId}
                />
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Additional Notes</Label>
            <Textarea
              value={formData.notes || ''}
              onChange={(e) => updateField('notes', e.target.value)}
              placeholder="Any additional information..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={loading}>
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {fine ? 'Update' : 'Create'} Fine
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Page Component
export const FineSchedulePage = () => {
  const { user, isAdmin, isSupervisor, isManagement } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  
  const [fines, setFines] = useState([]);
  const [pendingApproval, setPendingApproval] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [selectedFine, setSelectedFine] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [finesRes, pendingRes, statsRes, vehiclesRes, driversRes] = await Promise.all([
        finesAPI.list(),
        finesAPI.getPendingApproval(),
        finesAPI.getStatistics(),
        vehiclesAPI.list(),
        driversAPI.list(),
      ]);
      
      setFines(finesRes.data || []);
      setPendingApproval(pendingRes.data || []);
      setStatistics(statsRes.data || {});
      setVehicles(vehiclesRes.data || []);
      setDrivers(driversRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load fines data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateFine = async (data) => {
    try {
      let result;
      if (selectedFine) {
        result = await finesAPI.update(selectedFine.id, data);
        toast.success('Fine updated successfully');
      } else {
        result = await finesAPI.create(data, user.id, `${user.first_name} ${user.last_name}`);
        toast.success('Fine created successfully. Corrective action has been raised.');
      }
      setSelectedFine(null);
      fetchData();
      return result.data;
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 'Failed to save fine';
      toast.error(errorMsg);
      throw error;
    }
  };

  const handleSubmit = async (fine) => {
    try {
      await finesAPI.submit(fine.id, user.id, `${user.first_name} ${user.last_name}`);
      toast.success('Fine submitted for approval');
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 'Failed to submit fine';
      toast.error(errorMsg);
    }
  };

  const handleApprove = async (fine) => {
    try {
      await finesAPI.approve(fine.id, user.id, `${user.first_name} ${user.last_name}`);
      toast.success('Fine approved');
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 'Failed to approve fine';
      toast.error(errorMsg);
    }
  };

  const handleReturn = async (fine, reason) => {
    try {
      await finesAPI.return(fine.id, reason, user.id, `${user.first_name} ${user.last_name}`);
      toast.success('Fine returned for corrections');
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 'Failed to return fine';
      toast.error(errorMsg);
    }
  };

  const handleMarkPaid = async (fine) => {
    try {
      await finesAPI.markPaid(fine.id, user.id, `${user.first_name} ${user.last_name}`);
      toast.success('Fine marked as paid');
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 'Failed to mark fine as paid';
      toast.error(errorMsg);
    }
  };

  const handleDelete = async (fine) => {
    if (!window.confirm('Are you sure you want to delete this draft?')) return;
    try {
      await finesAPI.delete(fine.id);
      toast.success('Fine deleted');
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 'Failed to delete fine';
      toast.error(errorMsg);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const canApprove = isManagement || isAdmin;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="fines-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 
            className="text-3xl font-bold text-slate-900"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            FINE SCHEDULE
          </h1>
          <p className="text-slate-500 mt-1">
            Manage and track traffic fines
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline"
            onClick={() => finesAPI.exportExcel()}
            className="gap-2"
          >
            <Download className="w-4 h-4" />
            Export Excel
          </Button>
          <Button 
            onClick={() => {
              setSelectedFine(null);
              setFormDialogOpen(true);
            }}
            className="gap-2"
          >
            <Plus className="w-4 h-4" />
            New Fine
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Fines</p>
                  <p className="text-2xl font-bold">{statistics.total}</p>
                </div>
                <Receipt className="w-8 h-8 text-amber-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-red-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Unpaid</p>
                  <p className="text-2xl font-bold text-red-600">{statistics.unpaid_count}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-emerald-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Paid</p>
                  <p className="text-2xl font-bold text-emerald-600">{statistics.paid_count}</p>
                </div>
                <CheckCircle2 className="w-8 h-8 text-emerald-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Pending Approval</p>
                  <p className="text-2xl font-bold">{statistics.pending_approval_count}</p>
                </div>
                <Clock className="w-8 h-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-slate-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Unpaid Amount</p>
                  <p className="text-xl font-bold">R {statistics.unpaid_amount?.toLocaleString() || 0}</p>
                </div>
                <DollarSign className="w-8 h-8 text-slate-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All Fines ({fines.length})</TabsTrigger>
          {canApprove && (
            <TabsTrigger value="pending">
              Pending Approval ({pendingApproval.length})
            </TabsTrigger>
          )}
          <TabsTrigger value="unpaid">
            Unpaid ({fines.filter(f => !f.paid).length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-4">
          {fines.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700">No fines recorded</h3>
                <p className="text-slate-500 mt-1 mb-4">Create a new fine record to get started</p>
                <Button onClick={() => setFormDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  New Fine
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {fines.map((fine) => (
                <Card 
                  key={fine.id}
                  className={`hover:shadow-md transition-all cursor-pointer ${!fine.paid ? 'border-l-4 border-l-red-500' : ''}`}
                  data-testid={`fine-card-${fine.id}`}
                  onClick={() => {
                    setSelectedFine(fine);
                    setDetailDialogOpen(true);
                  }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${fine.paid ? 'bg-emerald-100' : 'bg-red-100'}`}>
                          <Receipt className={`w-5 h-5 ${fine.paid ? 'text-emerald-600' : 'text-red-600'}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold">{fine.registration_no || fine.truck_registration}</span>
                            <span className="text-slate-400">|</span>
                            <span className="font-mono text-slate-600">{fine.fine_number}</span>
                            <Badge className={fine.paid ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                              {fine.paid ? 'PAID' : 'UNPAID'}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-3 text-sm text-slate-500">
                            <span>
                              <Calendar className="w-3 h-3 inline mr-1" />
                              {fine.offence_date?.slice(0, 10)}
                            </span>
                            <span>
                              <MapPin className="w-3 h-3 inline mr-1" />
                              {fine.district || 'N/A'}
                            </span>
                            <span className="font-semibold">
                              R {fine.amount || 0}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={getStatusBadge(fine.status)}>
                          {getStatusLabel(fine.status)}
                        </Badge>
                        {fine.status === 'draft' && (
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedFine(fine);
                                setFormDialogOpen(true);
                              }}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSubmit(fine);
                              }}
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(fine);
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                        {fine.status === 'pending_approval' && canApprove && (
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-emerald-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleApprove(fine);
                              }}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                const reason = prompt('Reason for returning:');
                                if (reason) handleReturn(fine, reason);
                              }}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="pending" className="mt-4">
          {pendingApproval.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <CheckCircle2 className="w-12 h-12 text-emerald-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700">No pending approvals</h3>
                <p className="text-slate-500 mt-1">All fines have been reviewed</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {pendingApproval.map((fine) => (
                <Card key={fine.id} className="border-l-4 border-l-amber-500">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono font-bold">{fine.registration_no || fine.truck_registration}</span>
                          <span className="text-sm text-slate-500">
                            - {fine.fine_number} - R{fine.amount}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600">
                          Submitted by: {fine.submitted_by_name}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedFine(fine);
                            setDetailDialogOpen(true);
                          }}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Review
                        </Button>
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => handleApprove(fine)}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => {
                            const reason = prompt('Reason for returning:');
                            if (reason) handleReturn(fine, reason);
                          }}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Return
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="unpaid" className="mt-4">
          {fines.filter(f => !f.paid).length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <CheckCircle2 className="w-12 h-12 text-emerald-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700">All fines are paid</h3>
                <p className="text-slate-500 mt-1">No outstanding fines</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {fines.filter(f => !f.paid).map((fine) => (
                <Card key={fine.id} className="border-l-4 border-l-red-500">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                          <Receipt className="w-5 h-5 text-red-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold">{fine.registration_no || fine.truck_registration}</span>
                            <span className="font-mono text-slate-600">{fine.fine_number}</span>
                          </div>
                          <div className="flex items-center gap-3 text-sm text-slate-500">
                            <span>{fine.offence_date?.slice(0, 10)}</span>
                            <span className="font-bold text-red-600">R {fine.amount || 0}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedFine(fine);
                            setDetailDialogOpen(true);
                          }}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                        {fine.status === 'approved' && (
                          <Button
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => handleMarkPaid(fine)}
                          >
                            <CreditCard className="w-4 h-4 mr-1" />
                            Mark Paid
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Form Dialog */}
      <FineFormDialog
        open={formDialogOpen}
        onClose={() => {
          setFormDialogOpen(false);
          setSelectedFine(null);
        }}
        fine={selectedFine}
        onSave={handleCreateFine}
        vehicles={vehicles}
        drivers={drivers}
        onRefresh={fetchData}
      />

      {/* Detail Dialog */}
      <FineDetailDialog
        open={detailDialogOpen}
        onClose={() => {
          setDetailDialogOpen(false);
          setSelectedFine(null);
        }}
        fine={selectedFine}
        onApprove={handleApprove}
        onReturn={handleReturn}
        onMarkPaid={handleMarkPaid}
        canApprove={canApprove}
      />
    </div>
  );
};

export default FineSchedulePage;
